import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/images/lootka_logo.png";
import userIcon from "../assets/icons/usercircle-Vector.svg";
import ModalTemplate from "./ModalWrapper";
import SignUp from "./SignUp";
import Login from "./Login";
import { LogOut, Menu, UserIcon, X, Heart } from "lucide-react";
import useUserStore from "../store/userStore";
import { toast } from "react-toastify";
import type { User } from "../store/userStore";

const cities: string[] = ["رشت", "بندرانزلی", "ماسوله", "لاهیجان"];

const Navbar = () => {
  const [cityIndex, setCityIndex] = useState<number>(0);
  const [modalType, setModalType] = useState<"signup" | "login" | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState<boolean>(false);

  const { user, clearUser } = useUserStore();
  const navigate = useNavigate();
  const profileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCityIndex((prevIndex) => (prevIndex + 1) % cities.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isProfileMenuOpen &&
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  // Prevent body scroll when profile panel is open
  useEffect(() => {
    if (isProfileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isProfileMenuOpen]);

  const handleLogout = () => {
    clearUser();
    setIsProfileMenuOpen(false);
    navigate("/");
    setTimeout(() => {
      toast.success("با موفقیت خارج شدید", {
        className: "toast",
        hideProgressBar: true,
        position: "top-center",
      });
    }, 100);
  };

  const getDisplayName = (user: User): string => {
    if (!user) return "کاربر";
    const name = user.fullName?.trim() || user.username;
    if (!name) return "کاربر";
    const firstName = name.split(" ")[0];
    return firstName.length > 10 ? firstName.slice(0, 10) + "..." : firstName;
  };

  const getUserAvatar = (user: User) => {
    const imagePath = user?.image;
    if (!imagePath) return userIcon;
    if (imagePath.startsWith("http")) return imagePath;
    return `https://lootkatrip.ir${imagePath}`;
  };

  return (
    <>
      <nav className="absolute top-0 left-0 w-full z-20 h-[57px] px-[24px] tablet:px-20 desktop:px-36 shadow-lg overflow-visible">
        <div className="mx-auto h-full">
          <div className="flex justify-between items-center text-white relative h-full py-[10px]">
            {/* Right side buttons */}
            <div className="flex items-center desktop:gap-[28px] gap-[8px] text-sm font-myYekanMedium">
              {user ? (
                <div className="relative" ref={profileMenuRef}>
                  {/* User Button */}
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="bg-[#ffffff]/20 text-black font-myYekanMedium backdrop-blur-md border border-[0.6px] border-gray-400 px-2 py-2 rounded-full flex items-center gap-1 text-xs hover:bg-white/30 transition-colors"
                  >
                    <Menu size={14} className="text-black" />
                    <img
                      src={getUserAvatar(user)}
                      alt="avatar"
                      className="w-4 h-4 rounded-full border border-gray-200 object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = userIcon;
                      }}
                    />
                    <span>{getDisplayName(user)}</span>
                  </button>
                </div>
              ) : (
                <button
                  className="bg-[#ffffff]/20 text-black font-myYekanMedium backdrop-blur-md border border-[0.6px] border-gray-400 px-2 py-2 rounded-full flex items-center gap-1 text-xs"
                  onClick={() => setModalType("login")}
                >
                  <img src={userIcon} alt="user" className="w-4 h-4" />
                  ورود | ثبت نام
                </button>
              )}
            </div>

            {/* Centered Nav (desktop only) */}
            <div className="absolute left-1/2 -translate-x-1/2 desktop:flex hidden items-center desktop:gap-[32px] gap-4 text-[#222222] font-myYekanDemibold">
              <button onClick={() => navigate("/")}>خانه</button>
              <button onClick={() => navigate("/about")}>درباره ما</button>
              <button onClick={() => navigate("/contact")}>ارتباط با ما</button>
            </div>
            {/* Logo */}
            <div>
              <Link to="/">
                <div className="flex flex-row gap-[6px] items-center">
                  {/* <button
                    className="desktop:hidden flex items-center"
                    onClick={() => setIsMenuOpen(true)}
                  >
                    <Menu size={24} className="text-black" />
                  </button> */}
                  <img
                    src={logo}
                    alt="Logo"
                    className="desktop:w-[57px] desktop:h-[40px] w-[43px] h-[30px]"
                  />
                  <h2 className="text-[#4C3516] font-myYekanDemibold text-[16px] desktop:text-[24px]">
                    لوتکا
                  </h2>
                </div>
              </Link>
            </div>


          </div>
        </div>

        {/* Gradient separator */}
        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed top-0 left-0 w-full px-4 pb-4 bg-white shadow-lg flex flex-col items-center gap-6 pt-4 text-[#222222] font-myYekanDemibold z-40">
          <button
            className="self-end mr-6"
            onClick={() => setIsMenuOpen(false)}
          >
            <X size={16} />
          </button>
          <button onClick={() => { navigate("/"); setIsMenuOpen(false); }}>خانه</button>
          <button onClick={() => { navigate("/aboutUs"); setIsMenuOpen(false); }}>درباره ما</button>
          <button onClick={() => { navigate("/"); setIsMenuOpen(false); }}>قوانین و مقررات</button>
          <button onClick={() => { navigate("/contact"); setIsMenuOpen(false); }}>ارتباط با ما</button>
        </div>
      )}

      {/* Profile Side Panel */}
      {isProfileMenuOpen && (
        <div
          className="fixed inset-0 z-[100] transition-all duration-300"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          {/* Close Button */}
          <button
            onClick={() => setIsProfileMenuOpen(false)}
            className="absolute top-4 left-4 z-[101] flex items-center justify-center w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          >
            <X size={20} className="text-white" />
          </button>

          {/* Side Panel */}
          <div
            ref={profileMenuRef}
            className="absolute top-0 right-0 h-full w-[300px] bg-white shadow-2xl rounded-l-2xl overflow-y-auto"
          >
            {/* Header */}
            <div className="px-4 py-6 flex items-center gap-3 border-b border-gray-100">
              <img
                src={getUserAvatar(user!)}
                alt="avatar"
                className="w-14 h-14 rounded-full object-cover border-2 border-amber-500"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = userIcon;
                }}
              />
              <div>
                <p className="text-base font-bold text-gray-800">
                  {user?.fullName || user?.username || "کاربر"}
                </p>
                {user?.email && (
                  <p className="text-xs text-gray-500">{user?.email}</p>
                )}
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-2 font-myYekanRegular">
              <button
                onClick={() => { navigate("/userProfile"); setIsProfileMenuOpen(false); }}
                className="w-full text-right px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
              >
                <UserIcon size={18} className="text-gray-500" />
                پروفایل من
              </button>

              {/* <button
                onClick={() => { navigate("/favorites"); setIsProfileMenuOpen(false); }}
                className="w-full text-right px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
              >
                <Heart size={18} className="text-gray-500" />
                علاقه‌مندی‌ها
              </button> */}

              <div className="h-px bg-gray-100 my-2"></div>

              <button
                onClick={(e) => { e.stopPropagation(); handleLogout(); }}
                className="w-full text-right px-4 py-3 text-sm text-red-600 font-myYekanRegular hover:bg-red-50 flex items-center gap-3 transition-colors"
              >
                <LogOut size={18} className="text-red-500" />
                خروج از حساب
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for login/signup */}
      {modalType && (
        <ModalTemplate
          showModal={true}
          onClose={() => setModalType(null)}
          mainComponent={
            modalType === "login" ? (
              <Login
                onClose={() => setModalType(null)}
                switchToSignup={() => setModalType("signup")}
              />
            ) : (
              <SignUp
                onClose={() => setModalType(null)}
                switchToLogin={() => setModalType("login")}
              />
            )
          }
        />
      )}
    </>
  );
};

export default Navbar;